import { Button, TextField } from "@mui/material";
import produce from "immer";

import { TagColorKind, TagView } from "generated/graphql";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";
import { useMemo, useState } from "react";
import { Navigate } from "react-router";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { deleteTag, selectLensEditorLens, selectLensEditorTags, upsertTag } from "redux/slices/lensEditor";
import { RowSelectionState, createColumnHelper } from "@tanstack/react-table";
import { PTable } from "components/Table/PTable";
import { TagName } from "routes/lens-editor/tags/TagName";

import { TagDescription } from "./TagDescription";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { TagFields } from "./TagFields";
import { TagPreview } from "./TagPreview";
import PublishTagsModal from "../PublishTagsModal";
import CopyToClipboard from "components/CopyToClipboard";
import { isNotUndefined, parseSafeJSON } from "Backend";
import { compareTag } from "../../../components/tags/utils";
import { TagActions } from "./TagActions";
import { MaybeNewTag, TagCellExtra } from "./types";
import { TagSystemName } from "./TagSystemName";
import { TagRank } from "./TagRank";
import { enqueueSnackbar } from "notistack";
import { ExtractedGQLError } from "shared/PatternGraphQLError";

function createNewTag(existing: TagView[]): MaybeNewTag {
  const newCount = existing.filter((t) => t.systemName.startsWith("new_tag")).length;
  const systemName = `new_tag_${newCount}`;
  const rank = existing.reduce((maxRank, tag) => Math.max(maxRank, tag.rank), 0) + 1;

  return {
    isNewTag: true,
    systemName,
    tempSystemName: systemName,
    displayName: "New Tag",
    color: TagColorKind.Blue,
    colorValue: "#4080ee",
    description: null,
    fields: [],
    readOnlyFields: [],
    supportOnlyFields: [],
    filteredByDefaultFields: [],
    rank,
    tempRank: rank,
  };
}
const columnHelper = createColumnHelper<TagView>();

export function Tags() {
  const serverTags = useAppSelector(selectLensEditorTags);
  const [tags, setTags] = useState(serverTags);
  const { lensId } = useParamNumbers(["lensId"]);
  const lens = useAppSelector(selectLensEditorLens);
  const [search, setSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState<RowSelectionState>({});
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string>();
  const snackpatch = useAppDispatchWithSnackbar();
  const dispatch = useAppDispatch();
  const isEditing = (rowId: string) => editingRowId === rowId;

  const sortedTags = useMemo(() => tags.toSorted(compareTag), [tags]);

  const doUpsert = async (tag: MaybeNewTag) => {
    const { tempSystemName, tempRank, isNewTag, ...withoutClientFields } = tag;
    const payload = {
      ...withoutClientFields,
      systemName: (tag.isNewTag ? tag.tempSystemName : tag.systemName) || "",
      rank: tag.tempRank !== undefined ? tag.tempRank : tag.rank,
      lensId: lens.id,
    };
    setTags((tags) => tags.map((t) => (t.systemName === tag.systemName ? payload : t)).toSorted(compareTag));
    try {
      await snackpatch(upsertTag(payload), {
        successMessage: `Updated tag: ${tag.displayName}`,
        errorMessage: "Failed to update tag",
      });
    } catch (err) {
      // if there's an error, undo our optimistic update
      setTags((tags) => tags.map((t) => (t.systemName === payload.systemName ? tag : t)).toSorted(compareTag));
    } finally {
      setEditingRowId(undefined);
    }
  };

  const upsertAllTags = async (tags: MaybeNewTag[]) => {
    for (const t of tags) {
      await doUpsert(t);
    }
  };

  const doDelete = async (tag: MaybeNewTag) => {
    setTags((current) => {
      const index = current.findIndex((t) => t.systemName === tag.systemName);
      return produce(current, (draft) => {
        draft.splice(index, 1);
      });
    });
    if (tag.isNewTag) return;
    try {
      await dispatch(
        deleteTag({
          lensId: lens.id,
          systemName: tag.tempSystemName !== undefined ? tag.tempSystemName : tag.systemName,
        })
      ).unwrap();
      enqueueSnackbar(`Deleted ${tag.displayName}`, { variant: "success" });
      if (editingRowId === tag.systemName) setEditingRowId(undefined);
    } catch (err) {
      setTags((tags) => [...tags, tag].toSorted(compareTag));
      const backendError = (err as ExtractedGQLError)?.message || "Unknown Error";
      enqueueSnackbar(`Failed to delete ${tag.displayName}: ${backendError}`, { variant: "error" });
    }
  };

  const addTag = () => {
    const newTag = createNewTag(tags);
    setTags((t) => [...t, newTag]);
    setEditingRowId(newTag.systemName);
  };

  const updateTag = (tag: TagView) => {
    setTags((current) => {
      const index = current.findIndex((t) => t.systemName === tag.systemName);
      return produce(current, (draft) => {
        draft.splice(index, 1, tag);
      });
    });
  };

  const tagCellSharedProps: TagCellExtra = {
    setIsEditing: setEditingRowId,
    isEditing,
    updateTag,
    onSave: doUpsert,
  };

  // Memoizing the columns is important, typing in cells will trigger renders since we manage
  // table state at the top level
  const columns = useMemo(() => {
    return [
      columnHelper.accessor("displayName", {
        header: "Name",
        cell: (info) => <TagName {...info} {...tagCellSharedProps} />,
        // w-48
        size: 192,
        minSize: 192,
      }),
      columnHelper.accessor("systemName", {
        header: "System Name",
        cell: (info) => <TagSystemName {...info} {...tagCellSharedProps} />,
        size: 132,
        minSize: 132,
      }),
      columnHelper.accessor("description", {
        header: "Description",
        cell: (info) => <TagDescription {...info} {...tagCellSharedProps} />,
        size: 200,
        minSize: 200,
      }),
      columnHelper.accessor("rank", {
        header: "Rank",
        cell: (info) => <TagRank {...info} {...tagCellSharedProps} />,
        size: 48,
        minSize: 48,
      }),
      columnHelper.display({ header: "Preview", cell: TagPreview, minSize: 200 }),
      columnHelper.accessor("fields", {
        header: "Fields",
        cell: TagFields,
        size: 200,
        minSize: 200,
      }),
      columnHelper.display({
        header: "Actions",
        cell: (info) => <TagActions {...info} {...tagCellSharedProps} onDelete={doDelete} />,
        minSize: 150,
      }),
    ];
  }, [editingRowId]);

  if (lensId === undefined) {
    return <Navigate to=".." />;
  }

  const importFromClipboard = async () => {
    const clipboard = await navigator.clipboard.readText();

    const parsedTags = parseSafeJSON(clipboard) as TagView[];

    if (!parsedTags[0]?.systemName) return;

    setTags((existing) => {
      const tagMap = new Map(existing.map((t) => [t.systemName, t]));
      const parsedMap = new Map(parsedTags.map((t) => [t.systemName, t]));

      const nextState = [
        ...existing.map((t) => (parsedMap.has(t.systemName) ? parsedMap.get(t.systemName) : t)),
        ...parsedTags.filter((t) => !tagMap.has(t.systemName)),
      ].filter(isNotUndefined);

      upsertAllTags(nextState);

      return nextState;
    });
  };

  return (
    <div className="bg-white rounded-md shadow p-4">
      <div className="flex items-center justify-between pb-2">
        <div className="flex items-baseline p-0">
          <span className="p-1">
            <CopyToClipboard sx={{ p: 0, m: 0 }} text={JSON.stringify(tags)} />
          </span>
          <TextField label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outlined" onClick={() => importFromClipboard()}>
            Import Tags
          </Button>
          {lens.isFamilyPrimary && (
            <Button variant="outlined" onClick={() => setShowPublishModal(true)}>
              Publish to Lens Family
            </Button>
          )}
          <Button variant="contained" onClick={addTag}>
            Add Tag
          </Button>
        </div>
      </div>
      <div className="max-w-full">
        <PTable
          data={sortedTags}
          columns={columns}
          state={{ rowSelection: selectedTags, globalFilter: search }}
          globalFilterFn="includesString"
          onRowSelectionChange={setSelectedTags}
          getRowId={(row, _) => row.systemName}
        />
      </div>
      {showPublishModal && <PublishTagsModal open={true} close={() => setShowPublishModal(false)} lensId={lensId} />}
    </div>
  );
}
